import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Lightbulb, AlertTriangle, Sparkles, Target, BookOpenCheck, HelpCircle, Quote, CheckCircle2 } from "lucide-react";

const CALLOUTS = [
  { key: "💡", Icon: Lightbulb,     color: "hsl(148, 52%, 42%)", bg: "hsl(148, 52%, 96%)", label: "Dica" },
  { key: "⚠️", Icon: AlertTriangle,  color: "hsl(38, 92%, 40%)",  bg: "hsl(38, 92%, 96%)",  label: "Importante" },
  { key: "🎯", Icon: Target,         color: "hsl(262, 60%, 55%)", bg: "hsl(262, 60%, 97%)", label: "Objetivo" },
  { key: "📌", Icon: BookOpenCheck,  color: "hsl(210, 78%, 46%)", bg: "hsl(210, 78%, 96%)", label: "Resumo" },
  { key: "🧠", Icon: Sparkles,       color: "hsl(295, 55%, 52%)", bg: "hsl(295, 55%, 97%)", label: "Reflexão" },
  { key: "📚", Icon: Quote,          color: "hsl(220, 20%, 30%)", bg: "hsl(220, 20%, 96%)", label: "Exemplo" },
  { key: "✅", Icon: CheckCircle2,   color: "hsl(148, 60%, 32%)", bg: "hsl(148, 60%, 96%)", label: "Prática" },
  { key: "❓", Icon: HelpCircle,     color: "hsl(20, 80%, 50%)",  bg: "hsl(20, 80%, 97%)",  label: "Você sabia?" },
];

function extractText(node) {
  if (node == null) return "";
  if (typeof node === "string") return node;
  if (Array.isArray(node)) return node.map(extractText).join("");
  if (node.props && node.props.children) return extractText(node.props.children);
  return "";
}

function detectCallout(text) {
  if (!text) return null;
  const trimmed = text.trimStart();
  for (const c of CALLOUTS) {
    if (trimmed.startsWith(c.key)) {
      const rest = trimmed.slice(c.key.length).trim();
      return { ...c, text: rest };
    }
  }
  return null;
}

function CalloutBlockquote({ children }) {
  const raw = extractText(children);
  const callout = detectCallout(raw);
  if (!callout) {
    return (
      <blockquote className="my-5 pl-4 border-l-4 border-[hsl(var(--secondary))] italic text-[hsl(var(--muted-foreground))]">
        {children}
      </blockquote>
    );
  }
  const { Icon, color, bg, label } = callout;
  return (
    <aside
      className="my-6 rounded-2xl border-l-4 pl-5 pr-5 py-4 flex gap-3"
      style={{ borderLeftColor: color, background: bg }}
      data-testid={`callout-${label.toLowerCase()}`}
    >
      <div
        className="mt-0.5 h-8 w-8 rounded-full grid place-items-center text-white shrink-0"
        style={{ background: color }}
        aria-hidden="true"
      >
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-widest font-bold" style={{ color }}>{label}</div>
        <div className="text-[15px] leading-relaxed mt-1 [&_p]:m-0 [&_p+p]:mt-2">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{callout.text}</ReactMarkdown>
        </div>
      </div>
    </aside>
  );
}

function MdH1({ children }) { return <h1 className="font-heading text-3xl sm:text-4xl font-black mt-8 mb-4 leading-tight">{children}</h1>; }
function MdH2({ children }) {
  return (
    <h2 className="rich-h2 flex items-start gap-3 mt-10 mb-4 font-heading font-black text-2xl sm:text-3xl leading-tight">
      <span className="rich-h2-badge mt-1 shrink-0 h-8 min-w-[2rem] px-2.5 rounded-full bg-[hsl(var(--primary))] text-white font-heading font-black text-sm grid place-items-center" aria-hidden="true" />
      <span>{children}</span>
    </h2>
  );
}
function MdH3({ children }) { return <h3 className="mt-6 mb-3 font-heading font-black text-xl leading-tight">{children}</h3>; }
function MdP({ children }) { return <p className="my-4 leading-relaxed text-[15px]">{children}</p>; }
function MdUl({ children }) { return <ul className="my-4 space-y-2 pl-0 list-none">{children}</ul>; }
function MdOl({ children }) { return <ol className="my-4 space-y-2 pl-6 list-decimal marker:font-bold marker:text-[hsl(var(--primary))]">{children}</ol>; }
function MdLi({ children }) {
  return (
    <li className="pl-6 relative leading-relaxed text-[15px]">
      <span className="absolute left-0 top-2 h-2 w-2 rounded-full bg-[hsl(var(--primary))]" aria-hidden="true" />
      {children}
    </li>
  );
}
function MdStrong({ children }) { return <strong className="font-bold text-[hsl(var(--foreground))]">{children}</strong>; }
function MdEm({ children }) { return <em className="italic">{children}</em>; }
function MdA({ href, children }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="text-[hsl(var(--primary))] font-semibold underline underline-offset-2 hover:no-underline">
      {children}
    </a>
  );
}
function MdHr() {
  return (
    <div className="my-8 flex items-center gap-3">
      <div className="flex-1 h-px bg-[hsl(var(--border))]" />
      <Sparkles className="h-4 w-4 text-[hsl(var(--secondary))]" />
      <div className="flex-1 h-px bg-[hsl(var(--border))]" />
    </div>
  );
}
function MdCode({ inline, children }) {
  if (inline) return <code className="px-1.5 py-0.5 rounded bg-[hsl(var(--muted))] text-[13px] font-mono">{children}</code>;
  return <pre className="my-4 p-4 rounded-2xl bg-[hsl(var(--muted))] overflow-x-auto text-sm"><code>{children}</code></pre>;
}
function MdTable({ children }) {
  return (
    <div className="my-6 overflow-x-auto rounded-2xl border border-[hsl(var(--border))]">
      <table className="w-full text-sm">{children}</table>
    </div>
  );
}
function MdImg({ src, alt }) {
  return (
    <figure className="my-6">
      <img src={src} alt={alt || ""} className="w-full rounded-2xl border border-[hsl(var(--border))]" />
      {alt && <figcaption className="mt-2 text-xs text-[hsl(var(--muted-foreground))] text-center italic">{alt}</figcaption>}
    </figure>
  );
}

const COMPONENTS = {
  blockquote: CalloutBlockquote,
  h1: MdH1,
  h2: MdH2,
  h3: MdH3,
  p: MdP,
  ul: MdUl,
  ol: MdOl,
  li: MdLi,
  strong: MdStrong,
  em: MdEm,
  a: MdA,
  hr: MdHr,
  code: MdCode,
  table: MdTable,
  thead: ({ children }) => <thead className="bg-[hsl(var(--muted))]">{children}</thead>,
  th: ({ children }) => <th className="text-left px-4 py-2.5 font-heading font-bold">{children}</th>,
  td: ({ children }) => <td className="px-4 py-2.5 border-t border-[hsl(var(--border))]">{children}</td>,
  img: MdImg,
};

export default function RichCourseContent({ markdown }) {
  return (
    <div
      className="max-w-none [counter-reset:rich-h2] [&_.rich-h2]:[counter-increment:rich-h2] [&_.rich-h2-badge]:before:content-[counter(rich-h2)]"
      data-testid="rich-course-content"
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={COMPONENTS}>
        {markdown || ""}
      </ReactMarkdown>
    </div>
  );
}
